import { useEffect, useRef } from "react";
import { X } from "lucide-react";

export const BookingModal = ({ open, onClose, prefill, t, lang }) => {
  const ref = useRef(null);
  const baseUrl = process.env.REACT_APP_ZOHO_BOOKINGS_URL || "";

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => {
      const btn = ref.current?.querySelector('[data-testid="booking-close"]');
      btn?.focus();
    }, 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const params = new URLSearchParams();
  if (prefill?.name) params.set("name", prefill.name);
  if (prefill?.email) params.set("email", prefill.email);
  if (prefill?.phone) params.set("phone_number", prefill.phone);
  const sep = baseUrl.includes("?") ? "&" : "?";
  const finalUrl = baseUrl ? `${baseUrl}${sep}${params.toString()}` : "";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t.form.modal.title}
      data-testid="booking-modal"
      ref={ref}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(2, 42, 45, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1000px, 100%)",
          maxWidth: "100%",
          height: "min(720px, calc(100vh - 48px))",
          maxHeight: "calc(100vh - 48px)",
          background: "var(--white)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-lg)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <header
          className="flex items-center justify-between px-6 md:px-8 py-4"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <h3
            className="font-serif text-xl md:text-2xl"
            style={{ color: "var(--heading)" }}
          >
            {t.form.modal.title}
          </h3>
          <button
            type="button"
            data-testid="booking-close"
            onClick={onClose}
            aria-label={t.form.modal.close}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "transparent",
              border: "1px solid var(--border)",
              color: "var(--green-800)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "background var(--dur) var(--ease)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--paper)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <X size={18} />
          </button>
        </header>
        <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
          {finalUrl ? (
            <iframe
              data-testid="booking-iframe"
              title={t.form.modal.title}
              src={finalUrl}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
              allow="payment; clipboard-read; clipboard-write"
            />
          ) : (
            <div
              className="flex flex-col items-center justify-center text-center px-8"
              style={{ height: "100%", color: "var(--fg-muted)" }}
            >
              <p className="font-serif text-lg" style={{ color: "var(--heading)" }}>
                {t.form.modal.loading}
              </p>
              <p className="mt-3 text-sm">
                {lang === "uk"
                  ? "Зв'яжіться з нами безпосередньо: "
                  : "Reach us directly: "}
                <a
                  href={`mailto:${t.form.contactEmail}`}
                  className="under-link"
                  style={{ color: "var(--gold-500)" }}
                >
                  {t.form.contactEmail}
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
